import { ReactNode, useCallback, useState } from 'react';
import { useEventEmitter } from '../../lib/em/Hooks';
import { Consts, EditEventType } from '../../lib/em/EmitTypes';
import { EditType } from '../smartsend/item/EditType';
import { SmartSendItemsCache } from '../../lib/cache/SmartSendItemsCache';
import { ToSmartSendItem } from './ToSmartSendItem';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { Tagged } from '../Tagged';
import { P2GLinkGroup, P2GLink, P2GBox } from '@parcel2go/core-ui';
import { P2GSidePanel } from '~/components/sidepanel';
import { SearchObject } from '~/components/search/algolia/Models';
import Search from '~/components/search/algolia/Search';
import useGoto from '../../../../hooks/use-goto';
import { RoutingConstants } from '../../lib/consts/RoutingConstants';
import HitLayout from '~/components/search/algolia/HitLayout';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

export type SearchTabType = 'filter' | 'results';

const SearchTrigger = ({ label }: { label: ReactNode | string }) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { emit } = useEventEmitter<EditEventType>();
  const { goTo } = useGoto();
  const [open, setOpen] = useState(false);

  const edit = useCallback(
    (id: SearchObject, action: EditType) => {
      if (!SmartSendItemsCache.find((x) => x.Id == id.orderLineId.toString())) {
        SmartSendItemsCache.push(ToSmartSendItem(id));
      }
      emit(Consts.Edit, { Id: id.orderLineId.toString(), Action: action });
    },
    [emit]
  );

  const hitCb = useCallback(
    (hit: SearchObject) => (
      <HitLayout hit={hit}>
        <P2GLinkGroup
          links={[
            <P2GLink onClick={() => edit(hit, 'ORDER-ACTIONS')}>
              {t('SEARCH_TRIGGER_ORDER_ACTIONS', { defaultValue: 'Actions' })}
            </P2GLink>,
            <P2GLink onClick={() => edit(hit, 'ORDER-DETAILS')}>
              {t('SEARCH_TRIGGER_ORDER_DETAILS', { defaultValue: 'View Details' })}
            </P2GLink>,
            <P2GLink onClick={() => edit(hit, 'ORDER-ACTIONS')}>
              {t('SEARCH_TRIGGER_ORDER_LABELS', { defaultValue: 'View Labels' })}
            </P2GLink>,
            <P2GLink
              onClick={() => {
                goTo(`${RoutingConstants.Cancellations}/request/${hit.orderLineId}`);
                setOpen(false);
              }}
            >
              {t('SEARCH_TRIGGER_CANCEL', { defaultValue: 'Cancel' })}
            </P2GLink>,
            SettingsCache.settings.Features.includes('Tracking') && (
              <P2GLink onClick={() => edit(hit, 'ORDER-TRACKING')}>
                {t('SEARCH_TRIGGER_VIEW_TRACKING', { defaultValue: 'View Tracking' })}
              </P2GLink>
            )
          ]}
        />
      </HitLayout>
    ),
    [goTo, edit]
  );

  return (
    <>
      {typeof label === 'string' ? (
        <P2GLink onClick={() => setOpen(true)}>{label}</P2GLink>
      ) : (
        <P2GBox onClick={() => setOpen(true)}>{label}</P2GBox>
      )}
      <P2GSidePanel
        open={open}
        onClose={() => setOpen(false)}
        anchor="right"
        width="700px"
        title={t('SEARCH_TRIGGER_SEARCH_ORDERS_TITLE', { defaultValue: 'Search Order History' })}
      >
        {open && (
          <Tagged tag="panel-search-order-history">
            <Search hit={hitCb} />
          </Tagged>
        )}
      </P2GSidePanel>
    </>
  );
};

export default SearchTrigger;
